import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DataNode, DataEdge, DataFormat, Theme, ParsedData, TenantConfig } from '@/types'
import { parseContent, setValueByPath, serializeContent, parseJSON, parseYAML, parseTOML, parseXML } from '@/utils/parsers'
import { calculateLayout } from '@/utils/layout'

interface EditorState {
  // Content
  content: string
  format: DataFormat
  parsedData: unknown // Store the parsed object for editing

  // Graph
  nodes: DataNode[]
  edges: DataEdge[]

  // Metadata
  metadata: ParsedData['metadata'] | null

  // UI State
  isLoading: boolean
  error: string | null
  selectedNodeId: string | null

  // Settings
  theme: Theme
  layoutDirection: 'LR' | 'TB'
  showMinimap: boolean
  showControls: boolean

  // Tenant
  tenant: TenantConfig | null

  // Actions
  setContent: (content: string) => void
  setFormat: (format: DataFormat) => void
  parseAndRender: () => Promise<void>
  setSelectedNode: (nodeId: string | null) => void
  setTheme: (theme: Theme) => void
  setLayoutDirection: (direction: 'LR' | 'TB') => void
  toggleMinimap: () => void
  toggleControls: () => void
  setTenant: (tenant: TenantConfig) => void
  updateNodeValue: (path: string, value: unknown) => void
  reset: () => void
}

const initialState = {
  content: '',
  format: 'auto' as DataFormat,
  parsedData: null as unknown,
  nodes: [],
  edges: [],
  metadata: null,
  isLoading: false,
  error: null,
  selectedNodeId: null,
  theme: 'system' as Theme,
  layoutDirection: 'LR' as const,
  showMinimap: true,
  showControls: true,
  tenant: null,
}

export const useEditorStore = create<EditorState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setContent: (content) => set({ content }),
      
      setFormat: (format) => set({ format }),

      parseAndRender: async () => {
        const { content, format, layoutDirection } = get()

        if (!content.trim()) {
          set({ nodes: [], edges: [], metadata: null, error: null, parsedData: null })
          return
        }

        set({ isLoading: true, error: null })

        try {
          const parsed = await parseContent(content, format)

          const { nodes, edges } = calculateLayout(
            parsed.nodes,
            parsed.edges,
            { direction: layoutDirection }
          )

          // Store the parsed data for later editing
          const detectedFormat = parsed.metadata.format
          let parsedData: unknown = null
          try {
            if (detectedFormat === 'json') {
              parsedData = await parseJSON(content)
            } else if (detectedFormat === 'yaml') {
              parsedData = await parseYAML(content)
            } else if (detectedFormat === 'toml') {
              parsedData = await parseTOML(content)
            } else if (detectedFormat === 'xml') {
              parsedData = await parseXML(content)
            }
          } catch {
            // Keep parsedData as null if parsing fails
          }

          set({
            nodes,
            edges,
            metadata: parsed.metadata,
            parsedData,
            isLoading: false,
            error: null,
          })
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to parse content'
          set({
            isLoading: false,
            error: message,
            nodes: [],
            edges: [],
            metadata: null,
            parsedData: null,
          })
        }
      },

      setSelectedNode: (nodeId) => set({ selectedNodeId: nodeId }),

      setTheme: (theme) => {
        set({ theme })
        
        // Apply theme to document
        const root = document.documentElement
        if (theme === 'dark') {
          root.classList.add('dark')
        } else if (theme === 'light') {
          root.classList.remove('dark')
        } else {
          // System preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
          root.classList.toggle('dark', prefersDark)
        }
      },

      setLayoutDirection: async (direction) => {
        set({ layoutDirection: direction })
        
        const { nodes, edges } = get()
        if (nodes.length > 0) {
          const layouted = calculateLayout(nodes, edges, { direction })
          set({ nodes: layouted.nodes, edges: layouted.edges })
        }
      },

      toggleMinimap: () => set((state) => ({ showMinimap: !state.showMinimap })),
      
      toggleControls: () => set((state) => ({ showControls: !state.showControls })),

      setTenant: (tenant) => set({ tenant }),

      updateNodeValue: (path: string, value: unknown) => {
        const { parsedData, metadata, layoutDirection } = get()
        if (!parsedData || !metadata) return

        // Update the parsed data with the new value
        const newParsedData = setValueByPath(parsedData, path, value)

        // Serialize back to the original format
        const newContent = serializeContent(newParsedData, metadata.format)

        // Update content and re-parse
        set({ content: newContent, parsedData: newParsedData })

        // Re-render the graph with new data
        parseContent(newContent, metadata.format).then((parsed) => {
          const { nodes, edges } = calculateLayout(
            parsed.nodes,
            parsed.edges,
            { direction: layoutDirection }
          )
          set({
            nodes,
            edges,
            metadata: parsed.metadata,
          })
        })
      },

      reset: () => set(initialState),
    }),
    {
      name: 'dataviz-editor-storage',
      partialize: (state) => ({
        theme: state.theme,
        layoutDirection: state.layoutDirection,
        showMinimap: state.showMinimap,
        showControls: state.showControls,
      }),
    }
  )
)

// Subscribe to system theme changes
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', (e) => {
    const { theme } = useEditorStore.getState()
    if (theme === 'system') {
      document.documentElement.classList.toggle('dark', e.matches)
    }
  })
}
